import { useCallback, useEffect, useRef, useState } from 'react'

export type ZoomPan = {
  scale: number
  position: { x: number; y: number }
  isDragging: boolean
  animate: boolean
  setAnimate: (v: boolean) => void
  setScaleAnimated: (next: number) => void
  reset: () => void
  wheelRef: React.RefObject<HTMLDivElement>
  dragHandlers: {
    onMouseDown: (e: React.MouseEvent) => void
    onMouseMove: (e: React.MouseEvent) => void
    onMouseUp: () => void
  }
  wasClick: () => boolean
}

export function useZoomPan(): ZoomPan {
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [animate, setAnimate] = useState(false)

  const dragRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    startPosX: 0,
    startPosY: 0,
  })
  const clickRef = useRef(true)
  const wheelRef = useRef<HTMLDivElement>(null)

  const resetPosition = useCallback(() => setPosition({ x: 0, y: 0 }), [])

  const reset = useCallback(() => {
    setScale(1)
    setPosition({ x: 0, y: 0 })
  }, [])

  const setScaleAnimated = useCallback(
    (next: number) => {
      setAnimate(true)
      const clamped = Math.min(Math.max(next, 0.25), 3)
      setScale(clamped)
      if (clamped <= 1) resetPosition()
    },
    [resetPosition],
  )

  useEffect(() => {
    const el = wheelRef.current
    if (!el) return
    const handler = (e: WheelEvent) => {
      e.preventDefault()
      setAnimate(true)
      setScale((prev) => {
        const next =
          e.deltaY < 0 ? Math.min(prev + 0.05, 3) : Math.max(prev - 0.05, 0.25)
        if (next <= 1) resetPosition()
        return next
      })
    }
    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  }, [resetPosition])

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      clickRef.current = true
      if (scale <= 1) return
      e.preventDefault()
      setIsDragging(true)
      dragRef.current = {
        isDragging: true,
        startX: e.clientX,
        startY: e.clientY,
        startPosX: position.x,
        startPosY: position.y,
      }
    },
    [scale, position],
  )

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    const drag = dragRef.current
    if (!drag.isDragging) return
    clickRef.current = false
    setPosition({
      x: drag.startPosX + (e.clientX - drag.startX),
      y: drag.startPosY + (e.clientY - drag.startY),
    })
  }, [])

  const onMouseUp = useCallback(() => {
    dragRef.current.isDragging = false
    setIsDragging(false)
  }, [])

  const wasClick = useCallback(() => clickRef.current, [])

  return {
    scale,
    position,
    isDragging,
    animate,
    setAnimate,
    setScaleAnimated,
    reset,
    wheelRef,
    dragHandlers: { onMouseDown, onMouseMove, onMouseUp },
    wasClick,
  }
}
